package br.com.vvrefeicoes.portal.feature.rh

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.RhDismissed
import br.com.vvrefeicoes.portal.domain.model.RhOvertime
import br.com.vvrefeicoes.portal.domain.model.RhSummary
import br.com.vvrefeicoes.portal.domain.model.RhVacancies
import br.com.vvrefeicoes.portal.domain.repository.RhDashboardRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch

class RhPanelController(
    private val repository: RhDashboardRepository,
    private val scope: CoroutineScope,
) {
    var period by mutableStateOf<MonthPeriod?>(null)
        private set

    /** Diretoria selecionada; `null` consolida todas. */
    var director by mutableStateOf<String?>(null)
        private set

    var loading by mutableStateOf(true)
        private set

    var errorMessage by mutableStateOf<String?>(null)
        private set

    var summary by mutableStateOf<RhSummary?>(null)
        private set

    var dismissed by mutableStateOf<RhDismissed?>(null)
        private set

    var overtime by mutableStateOf<RhOvertime?>(null)
        private set

    var vacanciesLoading by mutableStateOf(false)
        private set

    var vacanciesError by mutableStateOf<String?>(null)
        private set

    var vacancies by mutableStateOf<RhVacancies?>(null)
        private set

    private var loadJob: Job? = null
    private var vacanciesJob: Job? = null

    fun applyPeriod(newPeriod: MonthPeriod) {
        if (period == newPeriod) return
        period = newPeriod
        reload()
    }

    fun selectDirector(value: String?) {
        if (value == director) return
        director = value
        reload()
    }

    fun retry() {
        reload()
    }

    /** As vagas não dependem da competência: são carregadas uma vez, ao abrir a aba. */
    fun loadVacancies(force: Boolean = false) {
        if (vacanciesJob?.isActive == true) return
        if (vacancies != null && !force) return
        vacanciesJob =
            scope.launch {
                vacanciesLoading = true
                vacanciesError = null
                try {
                    vacancies = repository.loadVacancies()
                } catch (exception: CancellationException) {
                    throw exception
                } catch (throwable: Throwable) {
                    vacanciesError = throwable.message ?: "Não foi possível carregar as vagas da RMP."
                }
                vacanciesLoading = false
            }
    }

    private fun reload() {
        val current = period ?: return
        val selectedDirector = director
        loadJob?.cancel()
        loadJob =
            scope.launch {
                loading = true
                errorMessage = null
                try {
                    coroutineScope {
                        val summaryRequest = async { repository.loadSummary(current, selectedDirector) }
                        val dismissedRequest = async { repository.loadDismissed(current, selectedDirector) }
                        val overtimeRequest = async { repository.loadOvertime(current, selectedDirector) }
                        summary = summaryRequest.await()
                        dismissed = dismissedRequest.await()
                        overtime = overtimeRequest.await()
                    }
                } catch (exception: CancellationException) {
                    throw exception
                } catch (throwable: Throwable) {
                    summary = null
                    dismissed = null
                    overtime = null
                    errorMessage = throwable.message ?: "Não foi possível carregar o painel de RH."
                }
                // Fora de um `finally`: uma carga cancelada não pode desligar o indicador da carga seguinte.
                loading = false
            }
    }
}

@Composable
fun rememberRhPanelController(): RhPanelController {
    val appGraph = LocalAppGraph.current
    val scope = rememberCoroutineScope()
    return remember { RhPanelController(appGraph.rhDashboardRepository, scope) }
}
